<script setup lang="ts">
import type { Props } from './type'

defineOptions({
  name: 'DistributeFeedSkeleton',
})

withDefaults(defineProps<Props>(), {
  aspectRatio: '3 / 4',
})
</script>

<template>
  <article class="feed-skeleton" aria-hidden="true">
    <div class="feed-skeleton__media" :style="{ '--skeleton-aspect': aspectRatio }" />

    <div class="feed-skeleton__copy">
      <span class="feed-skeleton__title" />
      <span class="feed-skeleton__author" />
    </div>
  </article>
</template>

<style scoped>
.feed-skeleton {
  display: inline-flex;
  width: 100%;
  max-width: 400px;
  flex-direction: column;
  gap: 0;
  padding: 12px 12px 16px;
  break-inside: avoid;
  vertical-align: top;
  --skeleton-base: var(--app-skeleton-base);
  --skeleton-line: var(--app-skeleton-line);
  --skeleton-shine: var(--app-skeleton-shimmer);
}

.feed-skeleton__media {
  position: relative;
  overflow: hidden;
  aspect-ratio: var(--skeleton-aspect);
  border-radius: 12px;
  background: var(--skeleton-base);
}

.feed-skeleton__media::before,
.feed-skeleton__title::before,
.feed-skeleton__author::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    110deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0) 38%,
    var(--skeleton-shine) 50%,
    rgba(255, 255, 255, 0) 62%,
    rgba(255, 255, 255, 0) 100%
  );
  transform: translateX(-120%);
  animation: feed-skeleton-shine 1.9s ease-in-out infinite;
}

.feed-skeleton__copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
}

.feed-skeleton__title {
  position: relative;
  overflow: hidden;
  width: 82%;
  height: 18px;
  border-radius: 6px;
  background: var(--skeleton-line);
}

.feed-skeleton__author {
  position: relative;
  overflow: hidden;
  width: 36%;
  height: 16px;
  border-radius: 6px;
  background: var(--skeleton-line);
}

@keyframes feed-skeleton-shine {
  0% {
    transform: translateX(-120%);
  }

  52%,
  100% {
    transform: translateX(120%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .feed-skeleton__media::before,
  .feed-skeleton__title::before,
  .feed-skeleton__author::before {
    animation: none;
  }
}
</style>
